import { DataTableActionBarAction } from '@/components/data-table/data-table-action-bar'
import { Button } from '@/components/ui/button'
import { Dialog, DialogTitle, DialogContent, DialogHeader, DialogFooter, DialogDescription } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useBulkCancelSubscription } from '@/lib/api/subscriptions'
import { Loader2, XCircle } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface CancelSubscriptionsBulkActionModalProps {
    subscriptionIds: string[]
}

export const CancelSubscriptionsBulkActionModal = ({ subscriptionIds }: CancelSubscriptionsBulkActionModalProps) => {
    const { t } = useTranslation('subscriptions')
    const { mutate: cancelSubscriptionsMutation, isPending: isCanceling } = useBulkCancelSubscription()
    const [isOpen, setIsOpen] = useState(false)
    const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(true)

    const handleSubmit = () => {
        cancelSubscriptionsMutation({ subscriptionIds, cancelAtPeriodEnd }, {
            onSettled: () => {
                setIsOpen(false)
            },
        })
    }

    const handleOpenChange = (open: boolean) => {
        if (!open && !isCanceling) setIsOpen(false)
    }

    return (
        <>
            <DataTableActionBarAction
                tooltip={t('bulkActions.cancel.tooltip')}
                isPending={isCanceling}
                onClick={() => setIsOpen(true)}
            >
                <XCircle className="mr-2 h-4 w-4" />
                {t('bulkActions.cancel.button')}
            </DataTableActionBarAction>
            <Dialog open={isOpen} onOpenChange={handleOpenChange}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>{t('bulkActions.cancel.title')}</DialogTitle>
                        <DialogDescription>
                            {t('bulkActions.cancel.confirmMessage', { count: subscriptionIds.length })}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex items-center justify-between space-x-2 py-4">
                        <Label htmlFor="cancel-at-period-end" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                            {t('bulkActions.cancel.cancelAtPeriodEnd')}
                        </Label>
                        <Switch id="cancel-at-period-end" checked={cancelAtPeriodEnd} onCheckedChange={setCancelAtPeriodEnd} />
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setIsOpen(false)} variant="outline">
                            {t('bulkActions.cancel.cancelButton')}
                        </Button>
                        <Button onClick={handleSubmit} disabled={isCanceling} variant="destructive">
                            {isCanceling && <Loader2 className="size-4 animate-spin mr-2" />}
                            {t('bulkActions.cancel.button')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}

